/**
 * Appearance engine: theme and interface density, applied for real.
 *
 * The persisted choices (localStorage "nuvra.theme" / "nuvra.density") are
 * applied at app startup (initAppearance from main.tsx), not only while the
 * Settings panel happens to be mounted, so a choice survives reloads on every
 * page. Theme "system" follows the OS scheme live via a matchMedia listener.
 * Density is real: html.density-compact shrinks the root font size, which
 * compacts every rem-based Tailwind spacing and type scale in the app.
 */

const THEME_KEY = "nuvra.theme";
const DENSITY_KEY = "nuvra.density";

export type AppearanceTheme = "dark" | "light" | "system";
export type AppearanceDensity = "default" | "compact";

function read(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}

function write(key: string, value: string): void {
  try { window.localStorage.setItem(key, value); } catch { /* private mode */ }
}

export function getStoredTheme(): AppearanceTheme {
  const v = read(THEME_KEY);
  return v === "light" || v === "system" ? v : "dark";
}

export function getStoredDensity(): AppearanceDensity {
  return read(DENSITY_KEY) === "compact" ? "compact" : "default";
}

function setThemeClasses(dark: boolean): void {
  const html = document.documentElement;
  html.classList.toggle("dark", dark);
  html.classList.toggle("light", !dark);
  html.setAttribute("data-theme", dark ? "dark" : "light");
}

export function applyTheme(theme: AppearanceTheme): void {
  if (theme === "system") {
    setThemeClasses(window.matchMedia("(prefers-color-scheme: dark)").matches);
  } else {
    setThemeClasses(theme === "dark");
  }
}

export function applyDensity(density: AppearanceDensity): void {
  const html = document.documentElement;
  html.setAttribute("data-density", density);
  html.classList.toggle("density-compact", density === "compact");
  html.classList.toggle("density-default", density !== "compact");
}

export function saveTheme(theme: AppearanceTheme): void {
  write(THEME_KEY, theme);
  applyTheme(theme);
}

export function saveDensity(density: AppearanceDensity): void {
  write(DENSITY_KEY, density);
  applyDensity(density);
}

let systemListener: ((e: MediaQueryListEvent) => void) | null = null;

/** Apply persisted appearance and keep "system" live. Idempotent. */
export function initAppearance(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  applyTheme(getStoredTheme());
  applyDensity(getStoredDensity());

  const media = window.matchMedia("(prefers-color-scheme: dark)");
  if (systemListener) media.removeEventListener("change", systemListener);
  systemListener = (e) => {
    if (getStoredTheme() === "system") setThemeClasses(e.matches);
  };
  media.addEventListener("change", systemListener);
}
